import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { SagaDefinitionEditor } from "../components/saga/SagaDefinitionEditor";
import { createSagaDefinition, getStepCatalog, getActiveSagaDefinition } from "../services/saga-definition";
import type { StepCatalogEntry, SagaStepDefinition } from "../types/saga-definition";

/**
 * Editor de criação. Suporta:
 * - `?clone=<name>`: pré-popula com a versão active dessa saga (criar nova versão).
 * - sem param: definition em branco.
 */
export default function SagaDefinitionNew() {
  const [params] = useSearchParams();
  const cloneOf = params.get("clone");
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<StepCatalogEntry[] | null>(null);
  const [initial, setInitial] = useState<{ name: string; description: string; steps: SagaStepDefinition[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cat = await getStepCatalog();
        if (!alive) return;
        setCatalog(cat);
        if (cloneOf) {
          try {
            const active = await getActiveSagaDefinition(cloneOf);
            if (alive) {
              setInitial({
                name: active.name,
                description: active.description ?? "",
                steps: active.steps,
              });
            }
          } catch {
            // sem active — usa só o name
            if (alive) setInitial({ name: cloneOf, description: "", steps: [] });
          }
        } else {
          setInitial({ name: "", description: "", steps: [] });
        }
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : "Falha ao carregar catálogo");
      }
    })();
    return () => { alive = false; };
  }, [cloneOf]);

  const handleSave = async (payload: { name: string; description: string; steps: SagaStepDefinition[] }) => {
    const created = await createSagaDefinition(payload);
    toast.success(`Definition '${created.name}' v${created.version} criada (active=false; ative manualmente)`);
    navigate(`/sagas/definitions/${encodeURIComponent(created.name)}`);
  };

  return (
    <>
      <PageHeader
        title={cloneOf ? `Nova versão de ${cloneOf}` : "Nova saga definition"}
        subtitle="Criada com active=false; ative manualmente após review"
        actions={
          <Link to="/sagas/definitions">
            <Button size="sm" variant="outline"><ArrowLeft size={14} /> Definitions</Button>
          </Link>
        }
      />
      <div className="p-6">
        {err && <div className="p-3 rounded-md bg-danger-soft text-danger-soft-fg text-sm mb-3">{err}</div>}
        {(!catalog || !initial) && !err && <p className="text-sm text-muted-foreground">Carregando catálogo…</p>}
        {catalog && initial && (
          <SagaDefinitionEditor
            catalog={catalog}
            initialName={initial.name}
            initialDescription={initial.description}
            initialSteps={initial.steps}
            onSave={handleSave}
          />
        )}
      </div>
    </>
  );
}
